import React, { Fragment, useState } from "react";
import "./robots-card.css";
import { Link } from "react-router-dom";
import { Badge, Button, Card, Space, Tag } from "antd";
import { useTranslation } from "react-i18next";
import RobotConnectionCardItem from "features/robot-teleop/components/robot-cards/robot-connection-item";
import RoboStatusSignal from "features/robot-teleop/components/robot-status/status/robot-status-signal";
import { servicesUrlConfig } from "shared/config/setting";

const getConnectingUserClientByRole = (robotGroup, roleName) => {
  if (!robotGroup || !robotGroup?.connecting_user_clients) {
    return [];
  }
  const seenUserClients = new Set();
  return robotGroup?.connecting_user_clients
    ?.filter((item) => {
      if (!item) {
        return false;
      }
      const [userClient, value] = item;
      if (!userClient || !value || !value.role) {
        return false;
      }
      if (value.role !== roleName) {
        return false;
      }
      if (seenUserClients.has(userClient)) {
        return false;
      }
      // userClientId format is "username#uniqueid", we only want to show unique usernames
      const username = userClient.split("#")[0];
      if (seenUserClients.has(username)) {
        return false;
      }
      seenUserClients.add(username);
      return true;
    })
    .map((item) => {
      const [userClient, value] = item;
      return {
        user_client_id: userClient,
        state: value.state,
        role: value.role,
      };
    });
};

const RobotCardItem = (props) => {
  const { t: translate } = useTranslation();
  const robotInfo = props.RobotInfo;
  const robotClientsGroup = props.RobotClientGroup || {};
  const connections = robotClientsGroup?.connections || [];
  const roles = props.Roles || [];

  const canControl = roles.includes("operator");
  const canMonitor = roles.length > 0; // any role can monitor
  const canSetting = roles.includes("admin") || roles.includes("operator");

  const robotID = robotInfo?.robot_id || "";

  const monitorUsers = getConnectingUserClientByRole(
    robotClientsGroup,
    "monitor",
  );
  const controlUsers = getConnectingUserClientByRole(
    robotClientsGroup,
    "controller",
  );
  const robotStatus = robotClientsGroup?.status || "offline";
  const isOnline = robotStatus.toLowerCase() === "online";

  const robotStatusSignal = robotClientsGroup?.statusSignal
    ? robotClientsGroup.statusSignal
    : controlUsers.length > 0
      ? "Teleoperation"
      : monitorUsers.length > 0
        ? "SharedAuto"
        : "NotConnected";

  const showConnections = props.ShowConnections;

  const onlineConnectionCount = connections.filter(
    (item) => item.status === "online",
  ).length;

  const shutdownRobotClick = props.shutdownRobotClick || (() => {});

  const restartRobotClick = props.restartRobotClick || (() => {});
  const settingRobotClick = props.settingRobotClick || (() => {});

  const showDeveloperLink = props.ShowDeveloperLink || (() => {});

  const [showOfflineRobots, setShowOfflineRobots] = React.useState(
    props?.ShowOfflineRobots,
  );

  return (
    <>
      {!showConnections && (
        <div className="card shadow-sm">
          <Badge.Ribbon
            text={
              !isOnline ? (
                ""
              ) : (
                <RoboStatusSignal
                  StatusSignal={robotStatusSignal}
                  NoBackground
                  NoBorder
                ></RoboStatusSignal>
              )
            }
            color={!isOnline ? "grey" : "black"}
            placement="start"
          >
            <Badge.Ribbon
              text={translate(`robots.${robotStatus}`)}
              color={robotStatus == "offline" ? "black" : "blue"}
              placement="end"
            >
              <svg
                className="bd-placeholder-img card-img-top"
                width="100%"
                height="125"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Placeholder: Thumbnail"
                preserveAspectRatio="xMidYMid slice"
                focusable="false"
              >
                <title>{robotID}</title>
                <rect
                  width="100%"
                  height="100%"
                  fill={robotStatus == "offline" ? "#55595c" : "#558c5c"}
                ></rect>
                <image
                  xlinkHref={servicesUrlConfig.ROBOTS_API.getStaticUrl(
                    robotInfo?.icon_path ??
                      robotInfo?.robot_type?.icon_path ??
                      "/robot_type_icons/unknown_robot_type.png",
                  )}
                  width="100%"
                  height="100%"
                  className={robotStatus == "offline" ? "disabled-image" : ""}
                />
              </svg>
            </Badge.Ribbon>
          </Badge.Ribbon>

          <div className="card-body">
            <h5 className="card-title">
              <b>
                {robotID} {onlineConnectionCount}/{connections.length}{" "}
                {!isOnline && `(${translate("robots.offline")})`}
              </b>
              <div>
                <Space direction="horizontal" size="middle">
                  <span className="d-flex justify-content-between align-items-left">
                    {translate("robots.monitors")} ({monitorUsers.length}):
                  </span>
                  {monitorUsers &&
                    monitorUsers.map(({ user_client_id, state, role }) => (
                      <Tag
                        key={user_client_id}
                        // className="d-flex align-items-center"
                        // color="blue" randomize color
                        color={
                          state == "connected"
                            ? "green"
                            : state == "connecting"
                              ? "orange"
                              : state == "disconnected"
                                ? "red"
                                : state == "closed"
                                  ? "gray"
                                  : "gray"
                        }
                      >
                        {user_client_id}/
                      </Tag>
                    ))}
                </Space>
              </div>
              <div>
                <Space direction="horizontal" size="middle">
                  <span className="d-flex justify-content-between align-items-left">
                    {translate("robots.controller")} ({controlUsers.length}):
                  </span>
                  {controlUsers &&
                    controlUsers.map(({ user_client_id, state, role }) => (
                      <Tag
                        key={user_client_id}
                        // className="d-flex align-items-center"
                        // color="blue" randomize color
                        color={
                          state == "connected"
                            ? "green"
                            : state == "connecting"
                              ? "orange"
                              : state == "disconnected"
                                ? "red"
                                : state == "closed"
                                  ? "gray"
                                  : "gray"
                        }
                      >
                        {user_client_id}/
                      </Tag>
                    ))}
                </Space>
              </div>
            </h5>
            <div className="d-flex justify-content-between align-items-center">
              <div className="btn-group">
                {isOnline && (
                  <Link
                    to={"/remote-control/" + robotID}
                    className={
                      "btn btn-sm btn-outline-secondary" +
                      (!canControl ? " disabled" : "")
                    }
                  >
                    {translate("robots.control")}
                  </Link>            
                  )}    
                {isOnline && (
                  <Link
                    to={"/remote-monitor/" + robotID}
                    className={
                      "btn btn-sm btn-outline-secondary" +
                      (!canMonitor ? " disabled" : "")
                    }
                  >
                    {translate("robots.monitor")}
                  </Link>
                )}
                <Link
                  to={"/robots/setting/" + robotClientsGroup.id}
                  className={
                    "btn btn-sm btn-outline-secondary" +
                    (!canSetting ? " disabled" : "")
                  }
                >
                  {translate("robots.setting")}
                </Link>

                {/* {isOnline && (
                  <Link
                    to="#"
                    type="button"
                    className={
                      "btn btn-sm btn-outline-secondary" +
                      (!isOnline ? " disabled" : "")
                    }
                    onClick={() => restartRobotClick(robot)}
                  >
                    {t('robots.restart')}
                  </Link>
                )} */}
                {/* {!isOnline && (
                  <Link
                    to="#"
                    type="button"
                    className={
                      "btn btn-sm btn-outline-secondary" +
                      (!isOnline ? " disabled" : "")
                    }
                    onClick={() => shutdownRobotClick(robot)}
                  >
                    {t('robots.shutdown')}
                  </Link>
                )} */}
              </div>
            </div>
          </div>
        </div>
      )}

      {showConnections &&
        connections &&
        connections.map((cnn) => (
          // (cnn.status === "online" || showOfflineRobots) &&
          <RobotConnectionCardItem
            {...props}
            key={cnn.id}
            ConnectionInfo={cnn}
          ></RobotConnectionCardItem>
        ))}
    </>
  );
};

export default RobotCardItem;
